import {
  BookOpen,
  Globe,
  SquareBottomDashedScissors,
  CodeXml,
  Image,
  Github,
  Slack,
  Blocks,
  FileText,
  MessageSquareText,
  FileType,
  MessageSquare,
  Video,
  FileSpreadsheet,
  FileJson,
  File,
} from 'lucide-react'
import { useTheme } from '@/context/theme-context'

/**
 * Get file type icon based on filename extension
 * Consolidated icon mapping for all file types
 */
export const FileExtensionIcon = ({
  filename,
  className = 'size-5',
  theme,
}: {
  filename: string
  className?: string
  theme?: 'light' | 'dark'
}) => {
  const ext = filename?.split('.').pop()?.toLowerCase() || ''

  switch (ext) {
    // Spreadsheet formats
    case 'csv':
      return <FileSpreadsheet className={`${className} text-green-600`} />
    case 'xlsx':
    case 'xls':
      return <FileSpreadsheet className={`${className} text-emerald-600`} />
    // Data formats
    case 'json':
      return <FileJson className={`${className} text-amber-600`} />
    // Document formats with custom icons
    case 'md':
    case 'mdx':
    case 'mxd':
      return (
        <img
          src={theme === 'dark' ? '/mxdIcon-dark.svg' : '/mxdIcon.svg'}
          alt='mxd'
          className={className}
        />
      )
    case 'pdf':
      return (
        <img
          src={theme === 'dark' ? '/pdfIcon-dark.svg' : '/pdfIcon.svg'}
          alt='pdf'
          className={className}
        />
      )
    case 'doc':
    case 'docx':
      return (
        <img
          src={theme === 'dark' ? '/docIcon-dark.svg' : '/docIcon.svg'}
          alt='doc'
          className={className}
        />
      )
    // Text formats
    case 'txt':
      return <FileType className={`${className} text-muted-foreground`} />
    // Image formats
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
      return <Image className={`${className} text-muted-foreground`} />
    // Default
    default:
      return <File className={`${className} text-muted-foreground`} />
  }
}

export const SourceTypeIcon = ({
  fileName,
  type,
}: {
  fileName: string
  type: string
}) => {
  const { theme } = useTheme()

  switch (type) {
    case 'Flow':
      return <Video className='text-muted-foreground size-5' />
    case 'Doc':
      return <BookOpen className='text-muted-foreground size-5' />
    case 'Website':
      return <Globe className='text-muted-foreground size-5' />
    case 'Screenshot':
      return (
        <SquareBottomDashedScissors
          size={20}
          className='text-muted-foreground'
        />
      )
    case 'Code':
      return <CodeXml className='text-muted-foreground size-5' />
    case 'UserUploadedFile':
      return <Image className='text-muted-foreground size-5' />
    case 'GitHubPullRequest':
    case 'GitHubRepository':
    case 'GitHubIssue':
    case 'GitHubCode':
      return <Github className='text-muted-foreground size-5' />
    case 'SlackMessage':
    case 'SlackThread':
      return <Slack className='text-muted-foreground size-5' />
    case 'TeamsMessage':
    case 'TeamsThread':
      return <MessageSquare className='text-muted-foreground size-5' />
    case 'Integration':
      return <Blocks className='text-muted-foreground size-5' />
    case 'ExternalDocs':
      return <FileText className='text-muted-foreground size-5' />
    case 'ChatMessage':
      return <MessageSquareText className='text-muted-foreground size-5' />
    default:
      // Use consolidated file extension icon
      // Handle 'system' theme by treating it as light (default)
      return <FileExtensionIcon filename={fileName} theme={theme === 'dark' ? 'dark' : 'light'} />
  }
}
